import confetti, { type Options } from 'canvas-confetti';
import { useMemo } from 'react';

const base: Options = { disableForReducedMotion: true };
const starColors = ['FFE400', 'FFBD00', 'E89400', 'FFCA6C', 'FDFFB8'];

type AnchorElement = Element | { current: Element | null } | null;

/** Element (or ref) to launch from, mapped to canvas-confetti's normalised viewport origin. */
function anchorOrigin(anchor?: AnchorElement): Pick<Options, 'origin'> {
  const el = anchor && 'current' in anchor ? anchor.current : anchor;
  if (!el || typeof window === 'undefined') return {};
  const r = el.getBoundingClientRect();
  return {
    origin: {
      x: (r.left + r.width / 2) / window.innerWidth,
      y: (r.top + r.height / 2) / window.innerHeight,
    },
  };
}

type CelebrateOptions = Options & { anchor?: AnchorElement };

export type Confetti = {
  /** Single burst — fire on a step completing. */
  celebrate: (options?: CelebrateOptions) => void;
  /** Staggered star/circle finale — fire on full completion. */
  finale: (options?: CelebrateOptions) => void;
};

export function useConfetti(): Confetti {
  return useMemo(
    () => ({
      celebrate: ({ anchor, ...options } = {}) =>
        confetti({ ...base, particleCount: 100, spread: 70, ...options, ...anchorOrigin(anchor) }),
      finale: ({ anchor, ...options } = {}) => {
        const star: Options = {
          ...base,
          spread: 360,
          ticks: 50,
          gravity: 0,
          decay: 0.94,
          startVelocity: 30,
          colors: starColors,
          ...options,
          ...anchorOrigin(anchor),
        };
        const shoot = () => {
          confetti({ ...star, particleCount: 40, scalar: 1.2, shapes: ['star'] });
          confetti({ ...star, particleCount: 10, scalar: 0.75, shapes: ['circle'] });
        };
        for (const delay of [0, 100, 200]) setTimeout(shoot, delay);
      },
    }),
    [],
  );
}
